import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  SCRUB_HOLD_GAP_MS,
  STARTUP_THRESHOLDS_MS,
  SCRUB_MAX_SPEED,
  scrubAdvance,
  scrubSpeedLabel,
  scrubStart,
  shouldOfferNext,
  timecode,
  watchedPercent,
  type GatewayError,
  type ScrubState,
} from '@player/domain';
import type { FocusPort, PlayerEvent, PlayerPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * La riproduzione di un film o di un episodio, nella forma del prototipo v3 (`sPlayer`, ramo `plVod`).
 *
 * ## Perché non è la schermata della diretta
 *
 * Perché un film **finisce**, e ha una posizione. La diretta non ha né l'una né l'altra: là la barra
 * mostra l'avanzamento del *programma* secondo l'EPG, i tasti sono «Guida TV» e «Canale ▲▼», e lo
 * zapping è il gesto principale. Qui la barra è la posizione dentro il file, i tasti sono `−10 s`,
 * `Pausa`, `+10 s`, e il gesto principale è fermarsi. Sono due grammatiche diverse, e fonderle darebbe
 * una schermata con dieci `if (isVod)` — cioè due schermate scritte in un posto solo.
 *
 * ## I minuti, non l'orologio
 *
 * Ai capi della barra il disegno scrive `6 min` e `110 min`, non `0:06:00` e `1:50:00`. È la domanda
 * che ci si fa guardando un film — «quanto manca» — e a quella i minuti rispondono senza farli
 * contare. Sulla diretta ai capi ci sono invece gli **orari** del programma, che sono un'altra
 * domanda.
 *
 * ## Dove si era arrivati si salva mentre si guarda, non alla fine
 *
 * Un film non finisce quasi mai con l'ultimo fotogramma: finisce con un televisore spento, con l'app
 * chiusa, con un tasto Indietro. Salvare «alla fine» significa non salvare mai nei casi che contano,
 * e riaprire un film alla scena iniziale dopo un'ora e mezza di visione. Si riferisce ogni
 * `PROGRESS_REPORT_MS`, più una volta all'uscita.
 */

/** Ogni quanto si riferisce dove si è arrivati. Trenta secondi: quel che si può perdere. */
export const PROGRESS_REPORT_MS = 30_000;

/**
 * Di quanto saltano i tasti `−10 s` e `+10 s`.
 *
 * Dieci e non trenta: sono i due tasti del disegno (`plBtns`), e dieci secondi è il salto con cui si
 * riascolta una battuta persa. Trenta era un numero mio.
 */
export const SEEK_STEP_SEC = 10;

export interface VodPlayerScreenProps {
  readonly focusPort: FocusPort;
  readonly createPlayer: (video: HTMLVideoElement) => PlayerPort;
  /** L'indirizzo da riprodurre. Cambiandolo si riparte: è il passaggio all'episodio dopo. */
  readonly streamUrl: string;
  /** Il titolo grande dell'OSD, e la riga sopra: «Film · Thriller», «Serie · Crime». */
  readonly title: string;
  readonly kicker: string;
  /** Da dove ripartire, in percentuale, oppure `null` per cominciare da capo. */
  readonly resumePercent: number | null;
  /** Dove si è arrivati: percentuale e secondi. Chi sta sopra lo archivia. */
  readonly onProgress: (percent: number, positionSec: number) => void;
  /** Il file è finito: chi sta sopra decide se passare all'episodio dopo o tornare alla scheda. */
  readonly onEnded: () => void;
  /** Se il titolo è già segnato come visto: cambia l'etichetta del tasto, non quel che fa. */
  readonly watched: boolean;
  readonly onToggleWatched: () => void;
  /** «Prossimo: S2 E4», a destra sotto i tasti. `null` per un film o per l'ultimo episodio. */
  readonly nextLabel?: string | null;
  /**
   * L'episodio successivo, per la card che compare negli ultimi tre minuti.
   *
   * `null` per un film e per l'ultimo episodio dell'ultima stagione. `startsSeason` cambia la frase
   * — «Inizia la stagione 3» invece di «Prossimo episodio» — perché cambia quel che si sta per fare:
   * una puntata dopo l'altra è continuare, una stagione nuova è cominciare.
   */
  readonly next?: { readonly label: string; readonly startsSeason: boolean; readonly season: number } | null;
  readonly onPlayNext?: () => void;
  readonly onExit: () => void;
  readonly onError: (error: GatewayError) => void;
}

/** Dove sta il fuoco nell'OSD. La barra è la prima, perché è quel che si apre l'OSD per fare. */
type Zone = 'next' | 'scrub' | 'buttons';

type Playback =
  /**
   * `sinceMs` c'è perché un avvio deve poter **finire**.
   *
   * Senza, questa schermata non aveva nessun tempo massimo: se il lettore non emette né il primo
   * fotogramma né un errore — ed è quel che succede quando il decoder dell'apparecchio ignora il
   * file in silenzio, cosa che Safari su iPhone fa — la scritta «Avvio…» restava lì **per sempre**.
   * La diretta questo limite ce l'ha da sempre (`stream.start_timeout`); il film no, e la differenza
   * non era una decisione: era una dimenticanza.
   */
  | { readonly kind: 'starting'; readonly sinceMs: number }
  | { readonly kind: 'playing' }
  | { readonly kind: 'paused' }
  | { readonly kind: 'failed'; readonly error: GatewayError };

export function VodPlayerScreen({
  focusPort,
  createPlayer,
  streamUrl,
  title,
  kicker,
  resumePercent,
  onProgress,
  onEnded,
  watched,
  onToggleWatched,
  nextLabel = null,
  next = null,
  onPlayNext,
  onExit,
  onError,
}: VodPlayerScreenProps): ReactNode {
  const translator = useTranslator();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<PlayerPort | null>(null);
  const [playback, setPlayback] = useState<Playback>({ kind: 'starting', sinceMs: Date.now() });
  const [osdVisible, setOsdVisible] = useState(false);
  const [position, setPosition] = useState({ positionSec: 0, durationSec: 0 });
  const [muted, setMuted] = useState(false);

  const handlers = useRef({ onProgress, onEnded, onExit, onError, onToggleWatched, onPlayNext });
  useEffect(() => {
    handlers.current = { onProgress, onEnded, onExit, onError, onToggleWatched, onPlayNext };
  }, [onProgress, onEnded, onExit, onError, onToggleWatched, onPlayNext]);

  /*
    Il cursore di anteprima, quando si sta scorrendo. `null` = la barra mostra la riproduzione.

    **Scorrere non riproduce.** Finché non si conferma, il film continua dov'è: un televisore che
    riparte da capo a ogni pressione della freccia impiega un secondo e mezzo a ricominciare, e
    scorrere diventa impossibile. Si conferma con OK, si annulla con Indietro.
  */
  const [scrub, setScrub] = useState<ScrubState | null>(null);

  /*
    Da quando si tiene premuto, e da quando è arrivato l'ultimo evento.

    Il telecomando non manda «premuto» e «rilasciato»: manda la stessa pressione ripetuta dalla
    ripetizione automatica del sistema. Tenere premuto e premere in fretta sono la stessa sequenza, e
    l'unica differenza misurabile è il buco fra un evento e il successivo — vedi `SCRUB_HOLD_GAP_MS`.

    In un riferimento e non nello stato: cambiano a ogni evento, e metterli nello stato
    ridisegnerebbe la schermata trenta volte al secondo per due numeri che nessuno guarda.
  */
  const holdStart = useRef(0);
  const lastKey = useRef(0);

  /** Chiusa da chi guarda: non torna fino al prossimo episodio. */
  const [nextDismissed, setNextDismissed] = useState(false);
  const [zone, setZone] = useState<Zone>('scrub');

  /*
    Il salto d'inizio si fa **una volta**, al primo istante utile.

    `seek` prima che la durata sia nota non porta da nessuna parte, e rifarlo a ogni `timeUpdate`
    riporterebbe indietro il film a ogni secondo: il riferimento è quel che distingue «riprendi» da
    «non avanza mai».
  */
  const resumed = useRef(false);
  const lastReport = useRef(0);

  /*
    La posizione anche in un riferimento, e **non è un doppione per comodità**.

    Lo stato serve a disegnare la barra; il riferimento serve alla pulizia dell'effetto, che nella
    chiusura vede il valore di *quando l'effetto è partito* — cioè zero. Scritta con lo stato, la riga
    che salva dove si era arrivati uscendo calcolerebbe sempre `watchedPercent(0, 0)`, cioè `null`,
    cioè niente: la funzione principale di questa schermata non avrebbe mai funzionato, e la prova
    superficiale — «esce senza errori» — sarebbe passata.
  */
  const positionRef = useRef({ positionSec: 0, durationSec: 0 });

  /*
    **Da dove riprendere si legge da un riferimento, non dalle dipendenze dell'effetto.**

    Qui stava il difetto peggiore di tutta la sezione on demand, ed è il terzo esemplare della stessa
    famiglia: un effetto che possiede un lettore non può elencare fra le dipendenze un valore che
    cambia mentre il lettore va.

    La catena: ogni trenta secondi si riferisce l'avanzamento → chi sta sopra lo archivia → `stored`
    cambia → `resumePercent` cambia → **l'effetto si ricrea**, `player.destroy()` e un `play()` nuovo.
    Il film ripartiva da capo ogni mezzo minuto. E non si riprendeva nemmeno, perché `resumed` è un
    riferimento che sopravvive alla ricreazione: alla seconda passata risultava già ripreso.

    Il sintomo che ne usciva era doppio e sembrava scollegato: «i film ripartono sempre dall'inizio» e
    «Continua a guardare è vuota». La seconda è conseguenza della prima — tornando a zero,
    `storablePercent` scarta il valore perché sotto la soglia minima, quindi non si archiviava mai
    niente.

    Adesso l'effetto dipende **solo da cosa riprodurre**: il lettore si ricrea quando cambia il film,
    e per nessun altro motivo.
  */
  const resumeRef = useRef(resumePercent);
  useEffect(() => {
    /*
      Si aggiorna **quando cambia il film**, non quando cambia l'avanzamento.

      Il punto di ripresa è una decisione che si prende aprendo: «riprendi dal 50%» vuol dire dal 50%
      di quando hai premuto, non dal valore che si sta scrivendo mentre guardi. Con `[resumePercent]`
      fra le dipendenze sarebbe la seconda cosa, e su un film riaperto di continuo il punto
      inseguirebbe sé stesso.
    */
    resumeRef.current = resumePercent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamUrl]);

  useEffect(() => {
    resumed.current = false;
    lastReport.current = 0;
    const video = videoRef.current;
    if (!video) return undefined;

    const player = createPlayer(video);
    playerRef.current = player;
    setPlayback({ kind: 'starting', sinceMs: Date.now() });
    setOsdVisible(false);
    setMuted(false);
    // Cambiando episodio si riparte puliti: un cursore di anteprima lasciato acceso punterebbe a un
    // minuto del file **precedente**, e la card del successivo chiusa non deve restare chiusa per
    // sempre — il successivo adesso è un altro.
    setScrub(null);
    setNextDismissed(false);
    setZone('scrub');

    const unsubscribe = player.subscribe((event: PlayerEvent) => {
      if (event.type === 'firstFrame' || event.type === 'playing') setPlayback({ kind: 'playing' });
      if (event.type === 'paused') setPlayback({ kind: 'paused' });
      if (event.type === 'mutedByPolicy') setMuted(true);
      if (event.type === 'ended') {
        /*
          Finito significa **visto**, e va detto prima di uscire.

          L'ultimo `timeUpdate` di un file arriva un po' prima della fine, quindi senza questa riga un
          film guardato per intero resterebbe archiviato al 98% — cioè per sempre in «Continua a
          guardare», che è la fascia che si riempie di cose finite e diventa inutile.
        */
        handlers.current.onProgress(100, positionRef.current.durationSec);
        handlers.current.onEnded();
      }
      if (event.type === 'timeUpdate') {
        positionRef.current = { positionSec: event.positionSec, durationSec: event.durationSec };
        setPosition(positionRef.current);

        // Si legge **una volta**, al primo `timeUpdate` utile: il valore di quando il film è stato
        // aperto, non quello che si sta scrivendo mentre lo si guarda.
        const resumeAt = resumeRef.current;
        if (!resumed.current && resumeAt !== null && event.durationSec > 0) {
          resumed.current = true;
          player.seek((event.durationSec * resumeAt) / 100);
          return;
        }

        const now = Date.now();
        if (now - lastReport.current < PROGRESS_REPORT_MS) return;
        lastReport.current = now;
        const percent = watchedPercent(event.positionSec, event.durationSec);
        if (percent !== null) handlers.current.onProgress(percent, event.positionSec);
      }
      if (event.type === 'error') {
        setPlayback({ kind: 'failed', error: event.error });
        handlers.current.onError(event.error);
      }
    });

    /*
      `'file'` e non il valore predefinito: un film è un file, non un manifesto.

      Vedi `MediaKind` nella porta. Senza questo argomento il lettore passerebbe da hls.js, che davanti
      a un MP4 cerca un manifesto e dichiara un errore di rete su un contenuto che stava arrivando.
    */
    player.play(streamUrl, 'file');

    return () => {
      unsubscribe();
      /*
        Uscendo si riferisce **dove si era**, e questa riga è quella che conta.

        È il caso normale: si esce con Indietro, non con l'ultimo fotogramma. Senza, l'avanzamento
        salvato sarebbe quello dell'ultimo riferimento periodico — fino a mezzo minuto prima — e su un
        film interrotto dopo venti secondi non ci sarebbe niente affatto.
      */
      const { positionSec, durationSec } = positionRef.current;
      const percent = watchedPercent(positionSec, durationSec);
      if (percent !== null) handlers.current.onProgress(percent, positionSec);
      player.destroy();
      playerRef.current = null;
    };
    // `resumePercent` **non** sta qui: vedi `resumeRef`. Elencarlo faceva ripartire il film da capo
    // a ogni salvataggio dell'avanzamento, cioè ogni trenta secondi.
  }, [createPlayer, streamUrl]);

  /*
    Oltre il tempo massimo non si aspetta più: si dichiara.

    È la stessa soglia della diretta — una sola, perché due numeri per «quanto è troppo» divergono
    al primo che qualcuno ritocca — e la stessa regola: un avvio che non finisce è un guasto, non
    un'attesa lunga.

    Il caso che l'ha resa necessaria non emette nessun errore. Safari su iPhone, davanti a un file
    che il suo decoder non sa aprire, a volte **non solleva niente**: nessun `error` sull'elemento,
    nessun fotogramma, nessun suono. Senza questa riga la schermata resta su «Avvio…» finché
    qualcuno non preme Indietro — e quel che si impara è «non funziona», che non è una diagnosi.

    `retryable` è vero: il motivo per cui un file non parte può benissimo essere passeggero — la
    rete del telefono, il worker occupato — e togliere il tasto «Riprova» a chi ha solo avuto
    sfortuna sarebbe peggio del contrario.
  */
  useEffect(() => {
    if (playback.kind !== 'starting') return undefined;
    const left = STARTUP_THRESHOLDS_MS.failed - (Date.now() - playback.sinceMs);
    const timer = setTimeout(
      () => {
        const error: GatewayError = {
          code: 'vod.start_timeout',
          params: {},
          retryable: true,
          atMs: Date.now(),
        };
        setPlayback({ kind: 'failed', error });
        handlers.current.onError(error);
      },
      Math.max(0, left),
    );
    return () => clearTimeout(timer);
  }, [playback]);

  /**
   * I tasti dell'OSD, nell'ordine del disegno (`plBtns`).
   *
   * Sono **tasti veri** e non un promemoria: è il prototipo a metterli lì, e su un telecomando senza
   * tasti dedicati sono l'unico modo di mettere in pausa o saltare sapendo cosa si sta premendo. Il
   * quarto cambia etichetta e non funzione — «Segna come visto» oppure «✓ Visto» — perché due tasti
   * diversi per la stessa azione costringono a cercare quale dei due c'è adesso.
   */
  const buttons = useMemo(
    () => [
      {
        id: 'back10',
        label: translator.t('vod.player.back10'),
        run: () => {
          const { positionSec } = positionRef.current;
          playerRef.current?.seek(Math.max(0, positionSec - SEEK_STEP_SEC));
        },
      },
      {
        id: 'pause',
        label: translator.t(playback.kind === 'paused' ? 'vod.player.resume' : 'vod.player.pause'),
        run: () => {
          const player = playerRef.current;
          if (!player) return;
          if (playback.kind === 'paused') player.resume();
          else player.pause();
        },
      },
      {
        id: 'forward10',
        label: translator.t('vod.player.forward10'),
        run: () => {
          const { positionSec, durationSec } = positionRef.current;
          playerRef.current?.seek(Math.min(durationSec, positionSec + SEEK_STEP_SEC));
        },
      },
      {
        id: 'watched',
        label: translator.t(watched ? 'vod.player.watched' : 'vod.action.markWatched'),
        run: () => handlers.current.onToggleWatched(),
      },
      { id: 'exit', label: translator.t('vod.player.exit'), run: () => handlers.current.onExit() },
    ],
    [translator, playback.kind, watched],
  );

  /** Su quale tasto è il fuoco, dentro la fila. La fila a fuoco la dice `zone`. */
  const [cursor, setCursor] = useState(0);

  /** Se la card del successivo è a schermo adesso: decide anche se `zone` può valere `'next'`. */
  const offeringNext =
    next !== null &&
    shouldOfferNext({
      hasNext: true,
      positionSec: position.positionSec,
      durationSec: position.durationSec,
      scrubbing: scrub !== null,
      dismissed: nextDismissed,
    });

  /*
    Aprire l'OSD e **cominciare a scorrere** sono la stessa cosa, quando si preme una freccia.

    È la regola del disegno — «la barra è il primo elemento a fuoco» — messa al posto giusto per la
    nostra scelta di tenere l'OSD chiuso mentre si guarda: chi preme ◀ o ▶ vuole spostarsi, non
    vedere l'OSD e poi spostarsi.
  */
  const pushScrub = useCallback(
    (direction: 1 | -1) => {
      const now = Date.now();
      const { positionSec, durationSec } = positionRef.current;
      const held = now - lastKey.current <= SCRUB_HOLD_GAP_MS;
      lastKey.current = now;

      setScrub((current) => {
        if (!held || current === null) {
          holdStart.current = now;
          return scrubStart(current, positionSec, durationSec, direction);
        }
        if (current.direction !== direction) {
          // Cambiando direzione si riparte da capo con l'accelerazione: continuare alla velocità di
          // prima, dall'altra parte, è il modo più sicuro di superare il punto che si cercava.
          holdStart.current = now;
          return scrubStart(current, positionSec, durationSec, direction);
        }
        return scrubAdvance(current, (now - holdStart.current) / 1000, (now - lastKey.current) / 1000, durationSec);
      });
    },
    [],
  );

  /** Conferma: la riproduzione va dove sta il cursore, e l'avanzamento si archivia subito. */
  const commitScrub = useCallback(() => {
    const target = scrub;
    if (target === null) return;
    setScrub(null);
    playerRef.current?.seek(target.positionSec);
    positionRef.current = { ...positionRef.current, positionSec: target.positionSec };
    setPosition(positionRef.current);
    const value = watchedPercent(target.positionSec, positionRef.current.durationSec);
    if (value !== null) handlers.current.onProgress(value, target.positionSec);
    // La card del successivo può tornare: saltare è un gesto che cambia quanto manca.
    setNextDismissed(false);
  }, [scrub]);

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        /*
          Indietro annulla prima lo scorrimento, poi chiude l'OSD, e solo dopo esce.

          Tre gradini e non due: uscire da un film per aver premuto una volta di troppo si paga con
          l'attesa di un riavvio, e perdere il punto che si stava cercando si paga con la stessa
          ricerca da rifare.
        */
        if (scrub !== null) {
          setScrub(null);
          return true;
        }
        if (osdVisible) {
          setOsdVisible(false);
          return true;
        }
        handlers.current.onExit();
        return true;
      }

      if (key.kind === 'select') {
        if (!osdVisible) {
          /*
            Il primo OK **apre** la barra; dal secondo in poi preme quel che è a fuoco.

            Se il primo OK premesse già qualcosa, aprire l'OSD per vedere quanto manca metterebbe in
            pausa il film: il gesto più innocuo diventerebbe quello che interrompe.
          */
          setOsdVisible(true);
          setZone('scrub');
          setCursor(0);
          return true;
        }
        if (zone === 'next') {
          if (cursor === 0) handlers.current.onPlayNext?.();
          else setNextDismissed(true);
          setZone('scrub');
          return true;
        }
        if (zone === 'scrub') {
          commitScrub();
          return true;
        }
        buttons[cursor]?.run();
        return true;
      }

      if (key.kind !== 'direction') return false;

      if (!osdVisible) {
        // A schermo pulito le frecce aprono l'OSD **e cominciano a scorrere**: è quel che chi preme
        // sta chiedendo, e farlo in due passaggi vorrebbe dire sprecare la prima pressione.
        if (key.direction === 'left' || key.direction === 'right') {
          setOsdVisible(true);
          setZone('scrub');
          setCursor(0);
          pushScrub(key.direction === 'right' ? 1 : -1);
        }
        return true;
      }

      if (zone === 'scrub' && (key.direction === 'left' || key.direction === 'right')) {
        pushScrub(key.direction === 'right' ? 1 : -1);
        return true;
      }

      if (key.direction === 'up') {
        // Uscire dalla barra **annulla** lo scorrimento: il cursore non può restare acceso su una
        // fila che non lo comanda più, o resterebbe lì a mentire.
        if (zone === 'buttons') {
          setZone('scrub');
          setCursor(0);
        } else if (zone === 'scrub' && offeringNext) {
          setScrub(null);
          setZone('next');
          setCursor(0);
        }
        return true;
      }

      if (key.direction === 'down') {
        if (zone === 'next') {
          setZone('scrub');
          setCursor(0);
        } else if (zone === 'scrub') {
          setScrub(null);
          setZone('buttons');
          setCursor(1);
        } else {
          setOsdVisible(false);
        }
        return true;
      }

      // Nelle file di tasti le frecce sono dei tasti: ai bordi si resta, non si avvolge.
      const width = zone === 'next' ? 2 : buttons.length;
      if (key.direction === 'left') setCursor((at) => Math.max(0, at - 1));
      if (key.direction === 'right') setCursor((at) => Math.min(width - 1, at + 1));
      return true;
    });
    /*
      `position` **non** sta fra le dipendenze, e il riferimento è lì per questo.

      Con lo stato qui dentro, l'effetto si rifarebbe a ogni `timeUpdate`: una sottoscrizione al
      telecomando disfatta e ricreata una volta al secondo per tutta la durata di un film. È la stessa
      radice dei tre cicli infiniti di fine settembre — un effetto che possiede una sottoscrizione e
      dipende da un valore che cambia da sé.

      `offeringNext` invece sì, e cambia di rado: dipende da `position`, ma solo attraverso una
      soglia — passa da falso a vero **una volta** per episodio, tre minuti prima della fine.
    */
  }, [focusPort, osdVisible, buttons, cursor, zone, scrub, offeringNext, pushScrub, commitScrub]);

  /** Dove si disegna il pallino: il cursore quando si sta scorrendo, altrimenti la riproduzione. */
  const shownSec = scrub?.positionSec ?? position.positionSec;
  const percent =
    position.durationSec > 0 ? Math.min(100, (shownSec / position.durationSec) * 100) : 0;
  /** La tacca bianca: da dove si era partiti. Serve a sapere quanto ci si è spostati. */
  const fromPercent =
    scrub !== null && position.durationSec > 0
      ? Math.min(100, (scrub.fromSec / position.durationSec) * 100)
      : null;
  const speed = scrub === null ? null : scrubSpeedLabel(scrub);

  return (
    <div
      data-vod-player="1"
      style={{ position: 'absolute', inset: 0, background: colors.playerBackground, overflow: 'hidden' }}
    >
      {/* `muted` non si dichiara qui: il padrone è l'adattatore. Vedi la diretta, dove costò l'audio. */}
      <video
        ref={videoRef}
        playsInline
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />

      {/*
        **Niente distintivo mentre si guarda.**

        Il prototipo mette `ON DEMAND` in alto a sinistra, e l'avevo copiato. Ma mentre un film va non
        deve esserci niente sopra l'immagine: è la stessa correzione già fatta sulla diretta, dove il
        distintivo rosso «IN DIRETTA» e il promemoria dei tasti sono stati tolti. Chi sta guardando un
        film sa che è un film — glielo dice il film.

        Quel che resta qui è solo ciò che **manca a chi guarda**: l'audio spento dalla piattaforma, con
        accanto il modo di rimediarlo. Un avviso senza il suo rimedio è una lamentela; un distintivo
        che ripete l'ovvio è ingombro.
      */}
      {muted && (
        <div
          data-vod-muted="1"
          style={{
            position: 'absolute',
            left: 72,
            top: 56,
            font: `600 22px ${FONT_STACK}`,
            color: colors.accent,
          }}
        >
          {translator.t('vod.player.mutedByPolicy')}
        </div>
      )}

      {/*
        L'episodio successivo, negli ultimi tre minuti.

        In alto a destra e non al centro: al centro coprirebbe la scena finale, che è esattamente
        quella per cui si è rimasti a guardare. «Nascondi» la chiude fino alla puntata dopo — non per
        sempre, perché l'episodio dopo è un altro e la domanda si ripone.
      */}
      {offeringNext && next !== null && (
        <div
          data-vod-next="1"
          style={{
            position: 'absolute',
            right: 72,
            top: 72,
            width: 520,
            padding: 28,
            borderRadius: 18,
            background: colors.surfaceRaised,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            boxShadow: 'var(--sh, 0 18px 48px rgba(0,0,0,.45))',
          }}
        >
          <span style={{ font: `700 20px ${FONT_STACK}`, color: colors.accent, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            {next.startsSeason
              ? translator.t('vod.player.nextSeason', { season: next.season })
              : translator.t('vod.player.nextEpisode')}
          </span>
          <span style={{ font: `700 30px ${FONT_STACK}`, color: colors.text }}>{next.label}</span>
          <span style={{ font: `500 22px ${FONT_STACK}`, color: colors.textDim }}>{title}</span>

          <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
            <button
              type="button"
              data-f="1"
              data-vod-next-play="1"
              data-focused={zone === 'next' && cursor === 0 ? '1' : '0'}
              onClick={() => handlers.current.onPlayNext?.()}
              style={{
                flex: 1,
                height: 64,
                border: 0,
                borderRadius: 12,
                cursor: 'pointer',
                background: colors.text,
                color: colors.background,
                font: `700 24px ${FONT_STACK}`,
                outline: zone === 'next' && cursor === 0 ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 4,
              }}
            >
              {translator.t('vod.player.nextPlay')}
            </button>
            <button
              type="button"
              data-f="1"
              data-vod-next-hide="1"
              data-focused={zone === 'next' && cursor === 1 ? '1' : '0'}
              onClick={() => setNextDismissed(true)}
              style={{
                flex: 'none',
                height: 64,
                padding: '0 26px',
                border: 0,
                borderRadius: 12,
                cursor: 'pointer',
                background: colors.surface,
                color: colors.text,
                font: `600 24px ${FONT_STACK}`,
                outline: zone === 'next' && cursor === 1 ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 4,
              }}
            >
              {translator.t('vod.player.nextHide')}
            </button>
          </div>
        </div>
      )}

      {playback.kind === 'starting' && (
        <div data-vod-starting="1" style={overlayStyle}>
          <span style={{ font: `600 34px ${FONT_STACK}`, color: colors.textMuted }}>
            {translator.t('vod.player.starting')}
          </span>
        </div>
      )}

      {playback.kind === 'failed' && (
        <div data-vod-player-error="1" style={overlayStyle}>
          <span style={{ font: `700 38px ${FONT_STACK}`, color: colors.errorText }}>
            {translator.t('vod.player.failed')}
          </span>
          {/*
            Il **motivo**, tradotto. Qui c'era `{playback.error.code}` nudo: a schermo compariva
            `stream.decoder_unsupported`, cioè un identificativo interno davanti a chi guarda.

            `t()` su una chiave mancante restituisce la chiave invece di fallire, quindi questo
            difetto non rompe niente e non finisce in nessun registro — ma qui la traduzione c'era in
            tutte e sette le lingue: a mancare era **la richiesta**, non la risposta. Tutte le altre
            schermate chiedono `errors.<codice>`; questa sola stampava il codice.
          */}
          <span style={{ font: `500 24px/1.4 ${FONT_STACK}`, color: colors.textMuted, paddingTop: 12, maxWidth: 900, textAlign: 'center' }}>
            {translator.t(`errors.${playback.error.code}`, playback.error.params)}
          </span>
          {/*
            E il codice con l'ora, in piccolo: non per chi guarda, per chi risponde al telefono.
            È la stessa riga che l'attivazione mostra sotto l'errore.
          */}
          <span style={{ font: '500 16px ui-monospace, Menlo, monospace', color: colors.textFaint, paddingTop: 8 }}>
            {translator.t('common.errorCodeAt', {
              code: playback.error.code,
              time: translator.time(playback.error.atMs),
            })}
          </span>
        </div>
      )}

      {osdVisible && (
        <div
          data-vod-osd="1"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '120px 72px 60px',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            background: 'var(--osd, linear-gradient(0deg,rgba(5,7,10,.96) 30%,rgba(5,7,10,0)))',
          }}
        >
          <span style={{ font: `600 26px ${FONT_STACK}`, color: colors.textMuted }}>{kicker}</span>
          <span style={{ font: `800 64px/1 ${FONT_STACK}`, letterSpacing: '-.02em' }}>{title}</span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {/*
              I **secondi**, non i minuti, e solo qui.

              «6 min» risponde a «quanto manca» e basta a chi guarda e basta. Per *scegliere* una
              posizione servono i secondi: senza, il cursore si muove e il numero resta fermo per
              cinquantanove secondi di fila, che si legge come una barra che non risponde.
            */}
            <span data-vod-elapsed="1" style={{ font: `600 22px ${FONT_STACK}`, color: colors.textMuted }}>
              {timecode(shownSec)}
            </span>

            <div
              data-f="1"
              data-vod-scrub="1"
              data-focused={zone === 'scrub' ? '1' : '0'}
              data-scrubbing={scrub !== null ? '1' : '0'}
              onClick={() => commitScrub()}
              style={{
                position: 'relative',
                flex: 1,
                height: 8,
                borderRadius: 4,
                background: colors.border,
                cursor: 'pointer',
                outline: zone === 'scrub' ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 10,
              }}
            >
              <div
                data-vod-progress="1"
                style={{ height: '100%', width: `${percent}%`, background: colors.accent, borderRadius: 4 }}
              />

              {/* La tacca bianca: da dove si era partiti, cioè dove Indietro riporta. */}
              {fromPercent !== null && (
                <span
                  data-vod-scrub-from="1"
                  style={{
                    position: 'absolute',
                    left: `${fromPercent}%`,
                    top: -4,
                    width: 3,
                    height: 16,
                    background: colors.text,
                  }}
                />
              )}

              {/* Il pallino: si vede quando la barra ha il fuoco, perché è allora che si può muovere. */}
              {zone === 'scrub' && (
                <span
                  data-vod-scrub-knob="1"
                  style={{
                    position: 'absolute',
                    left: `${percent}%`,
                    top: -8,
                    width: 24,
                    height: 24,
                    marginLeft: -12,
                    borderRadius: 12,
                    background: colors.text,
                  }}
                />
              )}

              {/*
                L'anteprima sopra il cursore: **orario e velocità**, senza il fotogramma.

                Il disegno ci mette anche l'immagine statica del fotogramma, e questa piattaforma non
                ce l'ha: servono le miniature di anteprima generate a monte (uno sprite ogni N
                secondi), che nessuno produce ancora. Disegnare qui la locandina al posto del
                fotogramma sarebbe peggio che lasciarlo vuoto — direbbe a chi guarda che a 32:10 c'è
                una scena che a 32:10 non c'è.

                Resta dentro i bordi della barra: un riquadro che esce dallo schermo ai capi è
                esattamente dove si va a cercare l'inizio e la fine.
              */}
              {scrub !== null && (
                <div
                  data-vod-scrub-preview="1"
                  style={{
                    position: 'absolute',
                    left: `${Math.min(92, Math.max(8, percent))}%`,
                    bottom: 34,
                    transform: 'translateX(-50%)',
                    minWidth: 220,
                    padding: '14px 18px',
                    borderRadius: 12,
                    background: colors.surfaceRaised,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span style={{ font: `800 34px ${FONT_STACK}`, color: colors.text }}>
                    {timecode(scrub.positionSec)}
                  </span>
                  {speed !== null && (
                    <span data-vod-scrub-speed="1" style={{ font: `600 20px ${FONT_STACK}`, color: colors.accent }}>
                      {speed.forward ? '▶▶ ' : '◀◀ '}
                      {speed.perSecond >= SCRUB_MAX_SPEED
                        ? translator.t('vod.player.scrubSpeedMax')
                        : translator.t('vod.player.scrubSpeed', { count: speed.perSecond })}
                    </span>
                  )}
                </div>
              )}
            </div>

            <span data-vod-duration="1" style={{ font: `600 22px ${FONT_STACK}`, color: colors.textMuted }}>
              {timecode(position.durationSec)}
            </span>
          </div>

          {/* Cosa fanno i tasti adesso: cambia fra «scorri» e «conferma», e senza questa riga il
              primo OK su una barra a fuoco è un salto nel buio. */}
          <span data-vod-scrub-hint="1" style={{ font: `500 20px ${FONT_STACK}`, color: colors.textFaint }}>
            {translator.t(scrub !== null ? 'vod.player.scrubConfirm' : 'vod.player.scrubHint')}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20, paddingTop: 8 }}>
            {buttons.map((button, index) => {
              const focused = index === cursor;
              return (
                <button
                  key={button.id}
                  type="button"
                  data-f="1"
                  data-osd-action={button.id}
                  data-focused={focused ? '1' : '0'}
                  onClick={button.run}
                  style={{
                    height: 68,
                    padding: '0 30px',
                    border: 0,
                    margin: 0,
                    borderRadius: 14,
                    cursor: 'pointer',
                    background: colors.surfaceRaised,
                    color: colors.text,
                    font: `600 25px ${FONT_STACK}`,
                    outline: focused ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 4,
                    transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                    transition: 'var(--tr, transform .16s ease)',
                  }}
                >
                  {button.label}
                </button>
              );
            })}
            {nextLabel !== null && nextLabel !== '' && (
              <span
                data-vod-next="1"
                style={{ marginLeft: 'auto', font: `500 24px ${FONT_STACK}`, color: colors.textDim }}
              >
                {nextLabel}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center' as const,
  padding: 40,
};
